import { useParams } from 'react-router';
import { ContextType } from 'api/conversations';
import { ProjectConversationId, ProjectId } from 'api/models';
import {
  ProjectConversation,
  ProjectGlobalConversation,
} from 'api/project-conversations';
import { useProjectGlobalConversationQuery } from 'queries/project-global-conversation/project-global-conversation';
import {
  useGetProjectConversationsQuery,
  useProjectConversationsQuery,
} from 'queries/project-wide-conversation';
import { GlobalConversationSeparator } from 'utils/urls';

interface Params {
  projectId: string;
  chatId: string;
}

interface EntityMeta {
  entityId?: ProjectGlobalConversation['entityId'];
  entityType?: ProjectGlobalConversation['entityType'];
}

const getEntityTypeFromUrl = (url: string): ContextType | undefined => {
  const [entityType] = url.split(GlobalConversationSeparator);
  const contextValues = Object.values(ContextType);
  const contextType = contextValues.find((val) => val === entityType);

  if (contextType) {
    return contextType as ContextType;
  }

  return undefined;
};

export const getGlobalConversationEntityMetaFromUrl = (
  chatId?: string,
): EntityMeta | undefined => {
  if (!chatId) {
    return undefined;
  }
  const [, _entityId] = chatId?.split(GlobalConversationSeparator);
  const entityId = Number(_entityId);
  const entityType = getEntityTypeFromUrl(chatId);

  if (Number.isFinite(entityId) && entityType) {
    return {
      entityType,
      entityId,
    };
  }

  return undefined;
};

export enum ConversationType {
  Global = 'Global',
  Project = 'Project',
}
export const useActiveConversationType = (): ConversationType => {
  const { chatId } = useParams<Params>();
  const { entityType } = getGlobalConversationEntityMetaFromUrl(chatId) || {};

  if (entityType) {
    return ConversationType.Global;
  }

  return ConversationType.Project;
};

export const useActiveProjectConversation = ():
  | ProjectConversation
  | undefined => {
  const { projectId, chatId } = useParams<Params>();
  const entityType = useActiveConversationType();
  const projId = ProjectId(Number(projectId));
  const conversationId = ProjectConversationId(Number(chatId));

  const { data: conversations = [] } = useProjectConversationsQuery(projId, {
    enabled: entityType === ConversationType.Project,
  });

  const activeConversation = conversations.find(
    (conv) => conv.conversationId === Number(chatId),
  );

  const { data: conversationDetails } = useGetProjectConversationsQuery(
    projId,
    conversationId,
    {
      enabled: entityType === ConversationType.Project,
    },
  );

  return {
    ...activeConversation,
    members: conversationDetails?.members || [],
  } as ProjectConversation;
};

export const useActiveGlobalConversation = (): {
  activeConversation: ProjectGlobalConversation | undefined;
  loading: boolean;
  id?: number;
} => {
  const { projectId: _projectId, chatId } = useParams<Params>();
  const { entityId, entityType } =
    getGlobalConversationEntityMetaFromUrl(chatId) || {};
  const projectId = ProjectId(Number(_projectId));
  const { data: activeConversation, isLoading } =
    useProjectGlobalConversationQuery(projectId, {
      enabled: !!entityType,
      select: (data) => {
        return data.conversations.find(
          (meta) =>
            meta.entityId === entityId && meta.entityType === entityType,
        );
      },
    });

  return {
    activeConversation,
    loading: isLoading,
    id: entityId,
  };
};
